import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "w-full pt-6" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("h1", { class: "text-dark-grey font-semibold text-xl my-6" }, "Subscriptions", -1)
const _hoisted_3 = {
  key: 0,
  class: "mt-10 border border-green-500 rounded-md p-4"
}
const _hoisted_4 = { class: "text-gray-900 font-semibold text-base mb-4" }
const _hoisted_5 = { class: "ml-4 inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-600 ring-1 ring-inset ring-green-500/20" }
const _hoisted_6 = { class: "grid grid-cols-4 gap-x-4 gap-y-8" }
const _hoisted_7 = {
  key: 1,
  class: "mt-10 border border-gray-300 rounded-md p-4"
}
const _hoisted_8 = { class: "text-gray-900 font-semibold text-base mb-4" }
const _hoisted_9 = { class: "ml-4 inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-500/20" }
const _hoisted_10 = { class: "grid grid-cols-4 gap-x-4 gap-y-8" }
const _hoisted_11 = {
  key: 2,
  class: "mt-6"
}
const _hoisted_12 = {
  key: 3,
  class: "mt-10"
}
const _hoisted_13 = /*#__PURE__*/_createElementVNode("h2", { class: "text-dark-grey font-semibold text-xl my-6" }, "New License", -1)
const _hoisted_14 = { class: "grid grid-cols-4 gap-x-4 gap-y-8" }
const _hoisted_15 = { class: "mt-4 flex justify-end gap-x-4" }
const _hoisted_16 = /*#__PURE__*/_createElementVNode("button", {
  type: "submit",
  class: "rounded-md bg-[#155EEF] px-2.5 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
}, " Submit ", -1)

import moment from 'moment';
import { computed, ref } from 'vue';
import { License } from '@/interfaces/license.interface';
import { useProductStore } from '@/stores/productStore';
import { useLicenseStore } from '@/stores/licenseStore';
import LearnersLicenseAllocationTable from '../LearnersLicenseAllocationTableComponent.vue';
import { School } from '@/interfaces/school.interface';


export default /*#__PURE__*/_defineComponent({
  __name: 'LicenseManagementComponent',
  props: {
    school: {}
  },
  setup(__props: any) {

const props = __props;

const productStore = useProductStore();
const licenseStore = useLicenseStore();

const newLicenseStartDate = ref();



const showNewLicenseForm = ref(false);
const showLearnersLicenseAllocationTable = ref(false);
const matrix = computed(() => licenseStore.licenseStateMatrix);

const availableProducts = computed(() => {
  return productStore.products;
});

const learnerLicenseAssignmentsComplete = computed(() => {
  if (!licenseStore.upcomingLicense) return false;
  const assignments = licenseStore.upcomingLicense.learnerProductAssignments || {};
  const hasNoAssignments = Object.keys(assignments).length === 0;
  return !hasNoAssignments; // true if assignments exist, false if none
});

// Determine validation rules for start and end dates depending on whether a current license exists
const startDateValidationRule = computed(() => {
  if (licenseStore.license) {
    // If there's a current license, start date must be after current license end date
    return `required|date_after:${moment(licenseStore.license.endDate).format('YYYY-MM-DD')}`;
  } else {
    // If no license, start date must be after today
    return `required|date_after:${moment().format('YYYY-MM-DD')}`;
  }
});

const startDateValidationMessage = computed(() => {
  if (licenseStore.license) {
    return 'Start date must be after current license end date';
  } else {
    return 'Start date must be after today';
  }
});

const endDateValidationRule = computed(() => {
  // End date must always be after start date
  // We'll handle this by referencing the startDate in the validation
  return `required|date_after_field:startDate`;
});

const validateAtLeastOneProduct = (formData: any) => {
  return availableProducts.value.some(product => formData[`product_${product._id}`] > 0);
};

const submitNewLicense = async (formData: any) => {
  if (!validateAtLeastOneProduct(formData)) {
    alert('At least one product must have a number of seats greater than 0');
    return;
  }

  const isRenewal = licenseStore.license ? true : false;
  
  const payload = {
    licenseType: 'annual',
    school: props.school._id,
    startDate: moment(formData.startDate).toISOString(),
    endDate: moment(formData.endDate).toISOString(),
    status: 'pending-learner-assignment',
    products: [],
  } as any;

  for (const product of availableProducts.value) {
    const seats = formData[`product_${product._id}`];
    if (seats) {
      payload.products.push({
        product: product._id,
        maxSeats: Number(seats),
        seatsUsed: 0,
      });
    }
  }

  await licenseStore.createLicense(payload, isRenewal);

  showNewLicenseForm.value = false;
}

const isEditing = ref(false);

const licenseStartDate = ref(moment(licenseStore.license?.startDate).format('YYYY-MM-DD'));
const licenseEndDate = ref(moment(licenseStore.license?.endDate).format('YYYY-MM-DD'));
const productSeats = ref(
  licenseStore.license?.products.reduce((acc, product) => {
    acc[product._id] = product.maxSeats;
    return acc;
  }, {} as Record<string, number>) || {}
);

const updateLicense = async () => {
  const payload = {
    startDate: moment(licenseStartDate.value).toISOString(),
    endDate: moment(licenseEndDate.value).toISOString(),
    products: [],
  };

  for (const product of licenseStore.license.products) {
    const seats = productSeats.value[product._id];
    if (seats) {
      payload.products.push({
        product: product._id,
        maxSeats: Number(seats),
        seatsUsed: product.seatsUsed,
      });
    }
  }

  await licenseStore.updateLicense(licenseStore.license._id, payload);
  isEditing.value = false;
};

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!
  const _component_modal = _resolveComponent("modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _hoisted_2,
      (_unref(licenseStore).license)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("h2", _hoisted_4, [
              _createTextVNode(" Current Subscription "),
              _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(licenseStore).license.status), 1)
            ]),
            (!isEditing.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (isEditing.value = true)),
                  class: "mb-4 px-2.5 py-2 rounded-md text-sm font-semibold text-[#155EEF] hover:text-white border border-[#155EEF] hover:bg-[#155EEF] focus:ring-4 focus:outline-none focus:ring-[#155EEF]"
                }, " Edit "))
              : _createCommentVNode("", true),
            _createVNode(_component_FormKit, {
              type: "form",
              actions: false,
              id: "subscriptions-form"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(_component_FormKit, {
                    readonly: !isEditing.value,
                    type: "date",
                    label: 'Licence Start Date',
                    placeholder: 'Licence Start Date',
                    modelValue: licenseStartDate.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((licenseStartDate).value = $event)),
                    classes: {
              'outer': 'col-span-2',
              'input': isEditing.value ? '' : 'bg-gray-300 cursor-not-allowed'
            }
                  }, null, 8, ["readonly", "modelValue", "classes"]),
                  _createVNode(_component_FormKit, {
                    readonly: !isEditing.value,
                    type: "date",
                    label: 'Licence End Date',
                    placeholder: 'Licence Start Date',
                    modelValue: licenseEndDate.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((licenseEndDate).value = $event)),
                    classes: {
              'outer': 'col-span-2',
              'input': isEditing.value ? '' : 'bg-gray-300 cursor-not-allowed'
            }
                  }, null, 8, ["readonly", "modelValue", "classes"]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(licenseStore).license.products, (product) => {
                    return (_openBlock(), _createBlock(_component_FormKit, {
                      readonly: !isEditing.value,
                      type: "number",
                      key: product._id,
                      label: `${product.title} Seats`,
                      placeholder: `${product.title} Licenses Seats`,
                      modelValue: productSeats.value[product._id],
                      "onUpdate:modelValue": ($event: any) => ((productSeats.value[product._id]) = $event),
                      validation: `min:${product.seatsUsed}`,
                      validationMessages: {
              min: `Seats cannot be less than ${product.seatsUsed}`
            },
                      classes: {
              'outer': 'col-span-2',
              'input': isEditing.value ? '' : 'bg-gray-300 cursor-not-allowed'
            }
                    }, null, 8, ["readonly", "label", "placeholder", "modelValue", "onUpdate:modelValue", "validation", "validationMessages", "classes"]))
                  }), 128))
                ])
              ]),
              _: 1
            }),
            (isEditing.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  type: "button",
                  onClick: updateLicense,
                  class: "mt-4 px-2.5 py-2 rounded-md text-sm font-semibold text-[#155EEF] hover:text-white border border-[#155EEF] hover:bg-[#155EEF] focus:ring-4 focus:outline-none focus:ring-[#155EEF]"
                }, " Update "))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      (_unref(licenseStore).upcomingLicense)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("h2", _hoisted_8, [
              _createTextVNode(" Upcoming Subscription "),
              _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(licenseStore).upcomingLicense.status), 1)
            ]),
            _createVNode(_component_FormKit, {
              type: "form",
              actions: false,
              id: "upcoming-license-form"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_10, [
                  _createVNode(_component_FormKit, {
                    readonly: "",
                    type: "date",
                    label: 'Licence Start Date',
                    placeholder: 'Licence Start Date',
                    value: _unref(moment)(_unref(licenseStore).upcomingLicense.startDate).format('YYYY-MM-DD'),
                    classes: {
              'outer': 'col-span-2',
              'input': 'bg-gray-300 cursor-not-allowed'
            }
                  }, null, 8, ["value"]),
                  _createVNode(_component_FormKit, {
                    readonly: "",
                    type: "date",
                    label: 'Licence End Date',
                    placeholder: 'Licence Start Date',
                    value: _unref(moment)(_unref(licenseStore).upcomingLicense.endDate).format('YYYY-MM-DD'),
                    classes: {
              'outer': 'col-span-2',
              'input': 'bg-gray-300 cursor-not-allowed'
            }
                  }, null, 8, ["value"]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(licenseStore).upcomingLicense.products, (product) => {
                    return (_openBlock(), _createBlock(_component_FormKit, {
                      readonly: "",
                      type: "number",
                      key: product._id,
                      label: `${product.title} Seats`,
                      placeholder: `${product.title} Licenses Seats`,
                      value: product.maxSeats,
                      classes: {
              'outer': 'col-span-2',
              'input': 'bg-gray-300 cursor-not-allowed'
            }
                    }, null, 8, ["label", "placeholder", "value"]))
                  }), 128))
                ])
              ]),
              _: 1
            }),
            (matrix.value.showAllocateLearnersButton)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  id: "allocateLearnersButton",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (showLearnersLicenseAllocationTable.value = true)),
                  class: "block mt-4 ml-auto mr-0 px-2.5 py-2 rounded-md text-sm font-semibold text-[#155EEF] hover:text-white border border-[#155EEF] hover:bg-[#155EEF] focus:ring-4 focus:outline-none focus:ring-[#155EEF] text-center me-2 mb-2"
                }, _toDisplayString(learnerLicenseAssignmentsComplete.value ? 'Review Product Licenses' : 'Allocate Product Licenses to learners'), 1))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      (matrix.value.showAddLicenseButton || (matrix.value.showExpiringSoonAlert && !_unref(licenseStore).upcomingLicense))
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            _createElementVNode("button", {
              id: "createLicenseButton",
              class: "block ml-auto mr-0 rounded-md bg-[#155EEF] px-2.5 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
              onClick: _cache[4] || (_cache[4] = ($event: any) => (showNewLicenseForm.value = true))
            }, " Add New License ")
          ]))
        : _createCommentVNode("", true),
      (showNewLicenseForm.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
            _hoisted_13,
            _createVNode(_component_FormKit, {
              type: "form",
              actions: false,
              onSubmit: submitNewLicense
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_14, [
                  _createVNode(_component_FormKit, {
                    type: "date",
                    name: "startDate",
                    modelValue: newLicenseStartDate.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((newLicenseStartDate).value = $event)),
                    label: "New License Start Date",
                    placeholder: "Select Start Date",
                    classes: { outer: 'col-span-2' },
                    validationMessages: {
              required: 'Start date is required',
              date_after: startDateValidationMessage.value,
            },
                    validation: startDateValidationRule.value
                  }, null, 8, ["modelValue", "validationMessages", "validation"]),
                  _createVNode(_component_FormKit, {
                    type: "date",
                    name: "endDate",
                    label: "New License End Date",
                    placeholder: "Select End Date",
                    validation: endDateValidationRule.value,
                    validationMessages: {
              required: 'End date is required',
              date_after: 'End date must be after start date',
            },
                    classes: { outer: 'col-span-2' }
                  }, null, 8, ["validation"]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableProducts.value, (product) => {
                    return (_openBlock(), _createBlock(_component_FormKit, {
                      type: "number",
                      key: product._id,
                      name: `product_${product._id}`,
                      label: `${product.name} Seats`,
                      placeholder: "Enter number of seats",
                      classes: { outer: 'col-span-2' }
                    }, null, 8, ["name", "label"]))
                  }), 128))
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "rounded-md bg-white px-2.5 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50",
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (showNewLicenseForm.value = false))
                  }, " Cancel "),
                  _hoisted_16
                ])
              ]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_component_modal, {
      showCloseButton: true,
      widthClass: 'max-w-5xl',
      open: showLearnersLicenseAllocationTable.value,
      onLocalModalOpen: _cache[8] || (_cache[8] = ($event: any) => (showLearnersLicenseAllocationTable.value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(LearnersLicenseAllocationTable, {
          gradeLevel: 7,
          licenseToAllocate: (_unref(licenseStore).upcomingLicense as License),
          onClose: _cache[7] || (_cache[7] = ($event: any) => (showLearnersLicenseAllocationTable.value = false))
        }, null, 8, ["licenseToAllocate"])
      ]),
      _: 1
    }, 8, ["open"])
  ], 64))
}
}

})